import { motion, AnimatePresence } from "framer-motion";
import { RecipeCard } from "./RecipeCard";
import { TextToSpeech } from "./TextToSpeech";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { findAmount, formatFraction } from "@/utils/recipeFormatters";

interface HealthyAlternative {
  original: string;
  alternative: string;
  note: string;
}

interface RecipeIngredientsListProps {
  ingredients: string[];
  healthyAlternatives?: HealthyAlternative[];
}

const ingredientInfo: Record<string, string> = {
  'flour': "Flour is like magic powder that makes our baked goods strong and fluffy! It's made from tiny grains of wheat. 🌾",
  'white sugar': "White sugar adds sweetness and helps baked goods turn golden brown. It has small, sparkly crystals that dissolve easily! ☀️",
  'brown sugar': "Brown sugar has molasses in it, which gives it a caramel flavor and makes cookies chewier and more moist! 🍯",
  'butter': "Butter makes everything rich and delicious. When it melts, it helps make things soft and moist! 🧈",
  'egg': "Eggs are like the glue that holds everything together. They also help things rise and become fluffy! 🥚",
  'milk': "Milk makes things creamy and soft. It also helps the dough or batter become smooth! 🥛",
  'salt': "Salt is like a superhero that makes all the other flavors stronger and tastier! 🦸‍♂️",
  'yeast': "Yeast is made of tiny living things that help bread rise by making bubbles - they're like tiny bakers! 🦠",
  'baking powder': "Baking powder is like a balloon inflator - it makes tiny air bubbles that help things rise! 🎈",
  'baking soda': "Baking soda is like a science experiment in your food - it makes bubbles when it meets something sour! 🧪",
  'vanilla': "Vanilla is like a sweet perfume that makes everything smell and taste amazing! 🌺",
  'chocolate': "Chocolate is made from special cocoa beans that grow on trees - it's like magic beans that make everything delicious! 🍫",
  'cinnamon': "Cinnamon comes from tree bark - it's like nature's sprinkles that make everything warm and cozy! ✨",
  'honey': "Honey is made by busy bees - they collect sweet nectar from flowers to make this natural sweetener! 🐝"
};

export const RecipeIngredientsList = ({ 
  ingredients, 
  healthyAlternatives = []
}: RecipeIngredientsListProps) => {
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});

  const handleCheck = (index: number, checked: boolean) => {
    setCheckedItems(prev => ({
      ...prev,
      [index]: checked
    }));
  };

  const getIngredientInfo = (ingredient: string): string | null => {
    const lowerIngredient = ingredient.toLowerCase();
    
    // First check for specific sugar types
    if (lowerIngredient.includes('white sugar')) {
      return ingredientInfo['white sugar'];
    } else if (lowerIngredient.includes('brown sugar')) {
      return ingredientInfo['brown sugar'];
    } else if (lowerIngredient.includes('sugar')) {
      return "Sugar makes things sweet and gives your baked goods a wonderful texture! ☀️";
    }
    
    // Then check for other ingredients
    return Object.entries(ingredientInfo).find(([key]) => 
      lowerIngredient.includes(key) && key !== 'white sugar' && key !== 'brown sugar')?.[1] || null;
  };

  const getAlternativeWithMeasurement = (ingredient: string): string | null => {
    const alternative = healthyAlternatives.find(alt => 
      ingredient.toLowerCase().includes(alt.original.toLowerCase())
    );

    if (!alternative) return null;

    const amount = findAmount(ingredient);
    if (!amount) return alternative.alternative;

    // For most alternatives, use the same measurement
    let convertedAmount = amount.amount;
    let convertedUnit = standardizeUnit(amount.unit);
    let altText = alternative.alternative;
    
    // Special conversion cases
    if (alternative.original.toLowerCase().includes('sugar') && 
        alternative.alternative.toLowerCase().includes('monk fruit')) {
      // Monk fruit sweetener is typically used in smaller amounts (about 1/3)
      convertedAmount = String(Number(amount.amount) / 3);
    }
    
    // Handle special case for butter to applesauce or mashed banana
    if (alternative.original.toLowerCase().includes('butter') && 
        alternative.alternative.toLowerCase().includes('applesauce or mashed banana')) {
      const formatted = formatFraction(Number(convertedAmount));
      return `${formatted} ${convertedUnit} unsweetened applesauce or ${Math.ceil(Number(convertedAmount))} mashed ripe banana${Number(convertedAmount) > 1 ? 's' : ''}`;
    }

    return `${formatFraction(Number(convertedAmount))} ${convertedUnit} ${altText}`;
  };

  const standardizeUnit = (unit: string): string => {
    const unitMap: Record<string, string> = {
      'tablespoon': 'tbsp',
      'tablespoons': 'tbsp',
      'tbsp': 'tbsp',
      'teaspoon': 'tsp',
      'teaspoons': 'tsp',
      'tsp': 'tsp',
      'cup': 'cup',
      'cups': 'cup',
      'ounce': 'oz',
      'ounces': 'oz',
      'oz': 'oz',
      'pound': 'lb',
      'pounds': 'lb',
      'gram': 'g',
      'grams': 'g',
      'g': 'g'
    };
    return unitMap[unit.toLowerCase()] || unit;
  };

  // Find the index of the first unchecked item
  const firstUncheckedIndex = ingredients.findIndex((_, index) => !checkedItems[index]);

  return (
    <RecipeCard title="Ingredients You'll Need" icon="🥄">
      <div className="space-y-6">
        <ul className="list-none space-y-4">
          {ingredients.map((ingredient: string, index: number) => {
            const info = getIngredientInfo(ingredient);
            const alternative = getAlternativeWithMeasurement(ingredient);
            return (
              <motion.li 
                key={index}
                className="text-base sm:text-lg break-words pl-8 relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-4">
                    <span className="absolute left-0 top-1">🌟</span>
                    <Checkbox 
                      id={`ingredient-${index}`}
                      checked={checkedItems[index]}
                      onCheckedChange={(checked) => handleCheck(index, checked as boolean)}
                      className="mt-1"
                    />
                    <label 
                      htmlFor={`ingredient-${index}`}
                      className={`flex-1 cursor-pointer ${checkedItems[index] ? 'line-through text-gray-400' : ''}`}
                    >
                      {ingredient}
                      {alternative && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-purple-600 text-sm mt-1 font-medium"
                        >
                          ↳ Replace with: {alternative}
                        </motion.div>
                      )}
                    </label>
                    <AnimatePresence>
                      {index === firstUncheckedIndex && (
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          className="shrink-0"
                        >
                          <TextToSpeech text={ingredient} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  {info && (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="ml-8 text-sm bg-purple-50 p-3 rounded-lg border border-purple-100"
                    >
                      <p className="flex items-start gap-2">
                        <span className="shrink-0">👩‍🔬</span>
                        {info}
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.li>
            );
          })}
        </ul>
      </div>
    </RecipeCard>
  );
};
